import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useBanners } from './BannerContext';
import { UpdateBanner } from '../components/ui/UpdateBanner';
import { ForceUpdateGate } from '../components/ui/ForceUpdateGate';
import {
  applyUpdate,
  checkForServiceWorkerUpdate,
  getPwaState,
  isServiceWorkerControlled,
  subscribePwa,
} from '../lib/pwa';

interface VersionState {
  updateAvailable: boolean;
  forceUpdate: boolean;
  latestVersion: string;
  dismissed: boolean;
  dismiss: () => void;
}

const VersionContext = createContext<VersionState>({
  updateAvailable: false,
  forceUpdate: false,
  latestVersion: '',
  dismissed: false,
  dismiss: () => {},
});

export function useVersion() {
  return useContext(VersionContext);
}

const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
const CURRENT_BUILD_HASH = typeof __BUILD_HASH__ !== 'undefined' ? __BUILD_HASH__ : '';

// Semver check: every 30 minutes (release notes, force updates)
const SEMVER_INTERVAL_MS = 30 * 60 * 1000;
// Build hash check: every 5 minutes (deploy detection)
const HASH_INTERVAL_MS = 5 * 60 * 1000;

const BANNER_ID = 'version-update';

/**
 * Can a refresh actually land on new code right now?
 *
 * With the service worker precaching index.html, a plain reload keeps serving
 * the *old* bundle until the new worker takes control. So we only auto-refresh
 * when either the new worker is waiting (applyUpdate swaps it in) or no worker
 * controls the page (a reload hits the network directly). Without this guard a
 * reload-on-navigate loop would be possible.
 */
function canReloadToNewVersion(): boolean {
  return getPwaState().needRefresh || !isServiceWorkerControlled();
}

export function VersionProvider({ children }: { children: ReactNode }) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [latestVersion, setLatestVersion] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const { addBanner, removeBanner } = useBanners();
  const semverIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const hashIntervalRef = useRef<ReturnType<typeof setInterval>>();

  // Build hash deploy detection
  const hashDetectedRef = useRef(false);
  const location = useLocation();
  const isInitialRoute = useRef(true);

  // Service worker: true once a newer worker is installed and waiting.
  // Used for the banner only — the auto-refresh effects below read live
  // state via getPwaState() so they fire on navigation/focus, never on the
  // moment the update happens to become ready (which could be mid-form).
  const { needRefresh } = useSyncExternalStore(subscribePwa, getPwaState, getPwaState);

  // ── Semver-based check (release notes, force updates) ─────────────────
  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch(`/api/version/check?platform=web&current=${APP_VERSION}`);
      if (!res.ok) return;
      const json = await res.json();
      if (!json.success) return;

      const { updateAvailable: ua, forceUpdate: fu, latestVersion: lv } = json.data;
      setUpdateAvailable(ua);
      setForceUpdate(fu);
      setLatestVersion(lv);
    } catch {
      // Fail silently — never block the user
    }
  }, []);

  // ── Build hash check (deploy detection) ───────────────────────────────
  const checkBuildHash = useCallback(async (): Promise<boolean> => {
    if (!CURRENT_BUILD_HASH) return false;
    if (hashDetectedRef.current) {
      // Already know a deploy is out; keep nudging the browser for the new
      // sw.js until the worker is actually waiting (the first update() can
      // miss, e.g. on a flaky connection).
      if (!getPwaState().needRefresh) void checkForServiceWorkerUpdate();
      return true;
    }
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return false;
      const json = await res.json();
      if (json.buildHash && json.buildHash !== CURRENT_BUILD_HASH) {
        hashDetectedRef.current = true;
        // Nudge the browser to fetch the new sw.js now rather than on its
        // own schedule, so the waiting worker (and needRefresh) show up fast.
        void checkForServiceWorkerUpdate();
        return true;
      }
    } catch {
      // version.json may not exist in dev — ignore
    }
    return false;
  }, []);

  // Semver: check on mount + periodic
  useEffect(() => {
    checkVersion();
    semverIntervalRef.current = setInterval(checkVersion, SEMVER_INTERVAL_MS);
    return () => clearInterval(semverIntervalRef.current);
  }, [checkVersion]);

  // Build hash: check on mount + periodic (every 5 min)
  useEffect(() => {
    checkBuildHash();
    hashIntervalRef.current = setInterval(checkBuildHash, HASH_INTERVAL_MS);
    return () => clearInterval(hashIntervalRef.current);
  }, [checkBuildHash]);

  // ── Auto-reload on route change if a new deploy was detected ──────────
  useEffect(() => {
    // Skip the initial route (app just loaded — nothing to reload for)
    if (isInitialRoute.current) {
      isInitialRoute.current = false;
      return;
    }
    if ((hashDetectedRef.current || getPwaState().needRefresh) && canReloadToNewVersion()) {
      void applyUpdate();
    }
  }, [location.pathname]);

  // ── Auto-reload on tab refocus if a new deploy is available ───────────
  useEffect(() => {
    async function onFocus() {
      checkVersion();
      const detected = await checkBuildHash();
      if ((detected || getPwaState().needRefresh) && canReloadToNewVersion()) {
        void applyUpdate();
      }
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [checkVersion, checkBuildHash]);

  // ── Banner (fallback while user stays on same page) ───────────────────
  const deployDetected = hashDetectedRef.current || needRefresh;
  const showBanner = deployDetected || (updateAvailable && !forceUpdate && !dismissed);

  useEffect(() => {
    if (forceUpdate) {
      removeBanner(BANNER_ID);
      return;
    }
    if ((updateAvailable || deployDetected) && !dismissed) {
      addBanner({
        id: BANNER_ID,
        priority: -10,
        content: (
          <UpdateBanner
            latestVersion={!updateAvailable ? '' : latestVersion}
            onRefresh={() => void applyUpdate()}
            onDismiss={() => setDismissed(true)}
          />
        ),
      });
    } else {
      removeBanner(BANNER_ID);
    }
  }, [showBanner, deployDetected, forceUpdate, updateAvailable, latestVersion, dismissed, addBanner, removeBanner]);

  return (
    <VersionContext.Provider value={{ updateAvailable: updateAvailable || deployDetected, forceUpdate, latestVersion, dismissed, dismiss: () => setDismissed(true) }}>
      {forceUpdate && <ForceUpdateGate onRefresh={() => void applyUpdate()} />}
      {children}
    </VersionContext.Provider>
  );
}
